import React, { ChangeEvent, useMemo, useRef, useState } from "react";
import { BusinessSsoOidcDto, BusinessSsoSamlDto, SSOType } from "lens-platform-sdk";
import { FormInput } from "@k8slens/lds-form";
import { misc } from "@k8slens/lds-icons";

import { base64ToXMLString, getIdentityProviderDetailsFromXML } from "src/utils/xml";

import TrackedButton from "src/components/Button/TrackedButton";
import Notification from "src/components/Notification/Notification";

import styles from "./SamlSSOProviderForm.module.css";
import { ButtonBar, Input } from "@k8slens/lds";

const { AwesomeIcon } = misc;

interface SSOFormProps {
  onSubmit: (settings: BusinessSsoSamlDto | BusinessSsoOidcDto) => void;
  loading: boolean;
}

const dataFieldDefaults = {
  singleSignOnServiceUrl: { value: "", valid: false },
  idpEntityId: { value: "", valid: false },
};

const reader = new FileReader();

export const SamlSSOProviderForm = ({ onSubmit, loading }: SSOFormProps) => {
  const [singleSignOnServiceUrl, setSingleSignOnServiceUrl] = useState(dataFieldDefaults.singleSignOnServiceUrl);
  const [idpEntityId, setIdpEntityId] = useState(dataFieldDefaults.idpEntityId);
  const [error, setError] = useState<Error | null>(null);
  const xmlInputRef = useRef<HTMLInputElement>(null);

  const valid = useMemo(
    () => [singleSignOnServiceUrl, idpEntityId].every(({ valid }) => valid !== false),
    [singleSignOnServiceUrl, idpEntityId],
  );

  const selectXMLFile = () => {
    setError(null);

    if (xmlInputRef.current) {
      // Clear the input before picking a new file
      xmlInputRef.current.value = "";
      xmlInputRef.current.click();
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    onSubmit({
      singleSignOnServiceUrl: singleSignOnServiceUrl.value,
      idpEntityId: idpEntityId.value,
      type: SSOType.SAML,
    });
  };

  const importFromXML = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target?.files;

    if (!file || !file[0]) {
      return;
    }

    reader.onload = () => {
      const resultString =
        reader.result && typeof reader.result !== "string" ? reader.result.toString() : reader.result;

      if (!resultString) {
        setError(new Error("File is empty. Please fill the form manually."));

        return;
      }

      try {
        const idpData = getIdentityProviderDetailsFromXML(base64ToXMLString(resultString));

        if (idpData.idpEntityId) {
          setIdpEntityId({ value: idpData.idpEntityId, valid: true });
        }

        if (idpData.singleSignOnServiceUrl) {
          setSingleSignOnServiceUrl({
            value: idpData.singleSignOnServiceUrl,
            valid: true,
          });
        }
      } catch (error) {
        setError(
          new Error("Can't parse metadata. Please fill the form manually.", {
            cause: error,
          }),
        );
      }
    };

    reader.readAsDataURL(file[0]);
  };

  return (
    <form aria-label="Saml SSO Provider settings" autoComplete="on" onSubmit={handleSubmit}>
      {error && (
        <Notification
          className={styles.error}
          level="error"
          message={error.message}
          type="flash"
          onClose={() => setError(null)}
        />
      )}
      <fieldset>
        <div className={styles.gridForm}>
          <div className={styles.xmlImport}>
            <Input
              type="file"
              ref={xmlInputRef}
              onChange={importFromXML}
              className={styles.xmlInput}
              accept="application/xml, text/xml"
            />
            <TrackedButton
              discreet
              loading={loading}
              label="Import metadata"
              type="ok"
              icon={AwesomeIcon}
              onClick={selectXMLFile}
              textTransform="capitalize"
            />
          </div>
          <FormInput
            wrapperProps={{ className: styles.singleSignOnServiceUrl }}
            label="Single sign-on Service URL"
            type="url"
            name="singleSignOnServiceUrl"
            placeholder="https://accounts.google.com/o/saml2/idp?idpid=xyz"
            value={singleSignOnServiceUrl.value}
            onChange={(value, valid) => setSingleSignOnServiceUrl({ value, valid })}
            required
          />
          <FormInput
            wrapperProps={{ className: styles.idpEntityId }}
            label="Identity provider Entity ID"
            type="url"
            name="idpEntityId"
            placeholder="https://accounts.google.com/o/saml2?idpid=xyz"
            value={idpEntityId.value}
            onChange={(value, valid) => setIdpEntityId({ value, valid })}
            required
          />
        </div>
        <ButtonBar type="grid" className={styles.buttonBar}>
          <TrackedButton
            label="Save single sign-on settings"
            onClick={handleSubmit}
            primary
            disabled={!valid}
            loading={loading}
            textTransform="capitalize"
          />
        </ButtonBar>
      </fieldset>
    </form>
  );
};
